<script setup lang="ts">
    import type { DataTableSelectOption } from '@/types';
    import DataTableSelect from '@/components/datatable/DataTableSelect.vue';
    import axios from 'axios';
    import { computed, ref } from 'vue';
    import { toast } from 'vue-sonner';
    import type { AcceptableValue } from 'reka-ui';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();

    const props = defineProps<{
        modelValue: AcceptableValue;
        onboardingStepId: number;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: AcceptableValue];
    }>();

    const currentValue = ref(props.modelValue);

    const options: DataTableSelectOption[] = [
        {
            value: 'completed',
            label: t('onboarding.statuses.completed'),
            color: 'green',
        },
        {
            value: 'rejected',
            label: t('onboarding.statuses.rejected'),
            color: 'red',
        },
        {
            value: 'pending',
            label: t('onboarding.statuses.pending'),
            color: 'transparent',
        },
    ];

    const handleStatusChange = async (newValue: AcceptableValue) => {
        const previousValue = currentValue.value;
        try {
            const selectedOption = options.find((opt) => opt.value === newValue);
            if (!selectedOption) return;
            currentValue.value = newValue;
            await axios.patch(
                route('v2.onboarding.update-status', {
                    companyOnboardingStep: props.onboardingStepId,
                }),
                {
                    status: newValue,
                },
            );
            emit('update:modelValue', newValue);
        } catch (error) {
            currentValue.value = previousValue;
            console.error('Failed to update status:', error);
            toast.error(t('onboarding.status_update_failed'));
        }
    };
</script>

<template>
    <DataTableSelect
        :model-value="currentValue"
        :options="options"
        @update:model-value="handleStatusChange"
    />
</template>
